import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, CheckCircle, Clock, Users, Award, FileText, Target, Calendar, Star, BookOpen, GraduationCap, Briefcase, MessageCircle } from 'lucide-react';

// Couleurs inspirées du thème petite enfance - douces et professionnelles
const theme = {
  primary: '#2D5A7B',      // Bleu professionnel
  secondary: '#E8B86D',    // Doré/jaune doux
  accent1: '#7BA38F',      // Vert doux
  accent2: '#D4A5A5',      // Rose poudré
  accent3: '#9BB1C9',      // Bleu clair
  background: '#FDF8F3',   // Crème très clair
  text: '#2C3E50',         // Texte foncé
  success: '#27AE60',      // Vert validation
  error: '#E74C3C',        // Rouge erreur
};

const slides = [
  {
    id: 1,
    title: "Réussir sa VAE CAP AEPE",
    subtitle: "Les étapes clés pour réussir votre validation des acquis de l'expérience",
    type: "title",
  },
  {
    id: 2,
    title: "Pourquoi choisir la VAE ?",
    type: "benefits",
    items: [
      "Valoriser votre expérience",
      "Obtenir un diplôme reconnu",
      "Évoluer professionnellement",
      "Accéder à des concours",
      "Augmenter votre employabilité"
    ]
  },
  {
    id: 3,
    title: "Conditions d'éligibilité",
    type: "eligibility",
    items: [
      "1 an d'expérience (1607h)",
      "Plus obligatoire mais fortement conseillé",
      "Activités auprès d'enfants de 0 à 6 ans",
      "Expérience salariée, bénévole ou indépendante"
    ]
  },
  {
    id: 4,
    title: "Les 7 étapes de la VAE",
    type: "steps",
    items: [
      { num: 1, text: "Vérifier son éligibilité" },
      { num: 2, text: "Déposer le Livret 1" },
      { num: 3, text: "Rédiger le Livret 2" },
      { num: 4, text: "Se faire accompagner" },
      { num: 5, text: "Déposer le dossier" },
      { num: 6, text: "Passer l'oral" },
      { num: 7, text: "Décision du jury" }
    ]
  },
  {
    id: 5,
    title: "Le Livret 1",
    type: "livret1",
    items: [
      "Dossier de recevabilité (étude de faisabilité)",
      "Justificatifs d'expérience",
      "Délai de réponse : environ 2 mois"
    ]
  },
  {
    id: 6,
    title: "Le Livret 2 : le cœur de la VAE",
    type: "livret2",
    blocs: [
      { title: "Bloc 1", desc: "Accompagner le développement du jeune enfant" },
      { title: "Bloc 2", desc: "Exercer son activité en accueil collectif" },
      { title: "Bloc 3", desc: "Exercer son activité en accueil individuel" }
    ],
    extras: [
      "Organisation au domicile",
      "Relation avec les parents",
      "Autonomie professionnelle"
    ]
  },
  {
    id: 7,
    title: "Conseils pour réussir",
    type: "tips",
    tips: [
      "Être précis et concret",
      "Analyser ses pratiques",
      "Utiliser le vocabulaire professionnel",
      "S'entraîner pour l'oral"
    ],
    example: {
      title: "Exemple de formulation attendue",
      wrong: "« Je fais des activités »",
      right: "« J'organise une activité de transvasement pour développer la motricité fine d'un enfant de 2 ans »"
    }
  },
  {
    id: 8,
    title: "Pourquoi se faire accompagner ?",
    subtitle: "L'oral devant le jury",
    type: "accompagnement",
    decisions: [
      { status: "success", text: "Validation totale", desc: "Vous obtenez votre diplôme !" },
      { status: "partial", text: "Validation partielle", desc: "Certains blocs sont validés" },
      { status: "retry", text: "Refus", desc: "Possibilité de retravailler le dossier et/ou l'oral" }
    ]
  },
  {
    id: 9,
    title: "Planning indicatif",
    type: "timeline",
    phases: [
      { phase: "Livret 1", duration: "1 mois" },
      { phase: "Livret 2", duration: "3 à 6 mois", note: "selon vos disponibilités" },
      { phase: "Jury", duration: "1 à 3 mois", note: "selon les sessions de votre région" }
    ],
    total: "Durée totale : 6 à 12 mois"
  },
  {
    id: 10,
    title: "Débouchés professionnels",
    type: "careers",
    jobs: [
      "Agent de crèche",
      "Auxiliaire parentale",
      "Garde d'enfants à domicile",
      "Agent en école maternelle",
      "Animateur petite enfance",
      "Assistant maternel"
    ]
  },
  {
    id: 11,
    title: "En résumé",
    type: "summary",
    points: [
      "La VAE est accessible et valorisante",
      "Elle demande rigueur et méthodologie",
      "Une bonne préparation = réussite"
    ]
  }
];

const SlideContent = ({ slide }) => {
  switch (slide.type) {
    case "title":
      return (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent3} 100%)`,
          borderRadius: '1rem',
          color: 'white'
        }}>
          <div style={{ 
            width: '100px', 
            height: '100px', 
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 2rem',
            border: '3px solid rgba(255,255,255,0.3)'
          }}>
            <GraduationCap size={50} style={{ color: 'white' }} />
          </div>
          <h2 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: 'white'
          }}>
            {slide.title}
          </h2>
          <p style={{ 
            fontSize: '1.25rem',
            opacity: 0.9,
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {slide.subtitle}
          </p>
        </div>
      );

    case "benefits":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '2rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '600px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                background: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.secondary}`
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: theme.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Star size={20} style={{ color: 'white' }} />
                </div>
                <span style={{ fontSize: '1.1rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "eligibility":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '2rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '600px', margin: '0 auto' }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1.25rem',
                background: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: `4px solid ${theme.accent1}`
              }}>
                <CheckCircle size={24} style={{ color: theme.accent1, flexShrink: 0 }} />
                <span style={{ fontSize: '1.05rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '2rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '1.25rem 1rem',
                background: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  background: theme.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.75rem',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '1.25rem'
                }}>
                  {item.num}
                </div>
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "livret1":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '2rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <div style={{ 
            maxWidth: '600px', 
            margin: '0 auto',
            background: 'white',
            borderRadius: '1rem',
            padding: '2rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '2rem'
            }}>
              <FileText size={60} style={{ color: theme.primary }} />
            </div>
            {slide.items.map((item, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                borderBottom: idx < slide.items.length - 1 ? `1px solid ${theme.accent3}30` : 'none'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: theme.secondary,
                  flexShrink: 0
                }} />
                <span style={{ fontSize: '1.05rem', color: theme.text }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "livret2":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '2rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '650px', margin: '0 auto' }}>
            {slide.blocs.map((bloc, idx) => (
              <div key={idx} style={{
                padding: '1.25rem',
                background: idx === 0 ? theme.primary : idx === 1 ? theme.accent1 : theme.accent2,
                borderRadius: '0.75rem',
                color: 'white'
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '0.5rem', fontSize: '1.1rem' }}>{bloc.title}</h4>
                <span style={{ fontSize: '0.95rem', opacity: 0.95 }}>{bloc.desc}</span>
              </div>
            ))}
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            justifyContent: 'center',
            marginTop: '1.5rem'
          }}>
            {slide.extras.map((extra, idx) => (
              <span key={idx} style={{
                padding: '0.5rem 1rem',
                background: 'white',
                borderRadius: '2rem',
                fontSize: '0.9rem',
                color: theme.text,
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
              }}>
                {extra}
              </span>
            ))}
          </div>
        </div>
      );

    case "tips":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '1.5rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '600px', margin: '0 auto 1.5rem' }}>
            {slide.tips.map((tip, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                background: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <Target size={22} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '1rem', color: theme.text }}>{tip}</span>
              </div>
            ))}
          </div>
          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            padding: '1.5rem',
            background: '#f8f9fa',
            borderRadius: '0.75rem',
            border: `2px solid ${theme.accent3}`
          }}>
            <p style={{ fontWeight: 'bold', marginBottom: '1rem', color: theme.primary, textAlign: 'center' }}>
              {slide.example.title}
            </p>
            <div style={{ 
              padding: '0.75rem 1rem',
              background: '#ffebee',
              borderRadius: '0.5rem',
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <span style={{ color: theme.error, fontWeight: 'bold', fontSize: '1.2rem' }}>✗</span>
              <span style={{ color: theme.error }}>{slide.example.wrong}</span>
            </div>
            <div style={{ 
              padding: '0.75rem 1rem',
              background: '#e8f5e9',
              borderRadius: '0.5rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <span style={{ color: theme.success, fontWeight: 'bold', fontSize: '1.2rem' }}>✓</span>
              <span style={{ color: theme.success }}>{slide.example.right}</span>
            </div>
          </div>
        </div>
      );

    case "accompagnement":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '0.5rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <p style={{ 
            textAlign: 'center', 
            marginBottom: '2rem',
            color: theme.text,
            fontSize: '1.1rem'
          }}>
            {slide.subtitle}
          </p>
          <h3 style={{ 
            textAlign: 'center',
            marginBottom: '1.5rem',
            color: theme.primary,
            fontSize: '1.2rem'
          }}>
            Décision du jury
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.decisions.map((decision, idx) => (
              <div key={idx} style={{
                padding: '1.25rem',
                background: decision.status === 'success' ? '#e8f5e9' : 
                           decision.status === 'partial' ? '#fff3e0' : 
                           '#ffebee',
                borderRadius: '0.75rem',
                borderLeft: `4px solid ${decision.status === 'success' ? theme.success : 
                                        decision.status === 'partial' ? '#ff9800' : 
                                        theme.error}`
              }}>
                <h4 style={{ 
                  fontWeight: 'bold', 
                  marginBottom: '0.25rem',
                  color: decision.status === 'success' ? theme.success : 
                         decision.status === 'partial' ? '#e65100' : 
                         theme.error
                }}>
                  {decision.text}
                </h4>
                <span style={{ fontSize: '0.9rem', color: theme.text }}>{decision.desc}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "timeline":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '2rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '550px', margin: '0 auto 2rem' }}>
            {slide.phases.map((phase, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1.25rem',
                background: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <Clock size={24} style={{ color: theme.primary }} />
                  <div>
                    <span style={{ fontWeight: 'bold', color: theme.text }}>{phase.phase}</span>
                    {phase.note && (
                      <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                        ({phase.note})
                      </p>
                    )}
                  </div>
                </div>
                <span style={{ 
                  fontWeight: 'bold',
                  color: theme.primary,
                  fontSize: '1.1rem'
                }}>
                  {phase.duration}
                </span>
              </div>
            ))}
          </div>
          <div style={{
            padding: '1.25rem',
            background: theme.primary,
            borderRadius: '0.75rem',
            textAlign: 'center',
            maxWidth: '550px',
            margin: '0 auto'
          }}>
            <span style={{ 
              fontWeight: 'bold',
              color: 'white',
              fontSize: '1.25rem'
            }}>
              {slide.total}
            </span>
          </div>
        </div>
      );

    case "careers":
      return (
        <div style={{ padding: '1rem' }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '2rem', 
            textAlign: 'center',
            color: theme.primary
          }}>
            {slide.title}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            {slide.jobs.map((job, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}>
                <Briefcase size={20} style={{ color: theme.secondary, flexShrink: 0 }} />
                <span style={{ fontSize: '0.95rem', color: theme.text }}>{job}</span>
              </div>
            ))}
          </div>
        </div>
      );

    case "summary":
      return (
        <div style={{ 
          padding: '2rem',
          background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent3} 100%)`,
          borderRadius: '1rem',
          textAlign: 'center'
        }}>
          <h2 style={{ 
            fontSize: '1.75rem', 
            fontWeight: 'bold',
            marginBottom: '2rem',
            color: 'white'
          }}>
            {slide.title}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '500px', margin: '0 auto' }}>
            {slide.points.map((point, idx) => (
              <div key={idx} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '0.75rem',
                backdropFilter: 'blur(10px)'
              }}>
                <CheckCircle size={24} style={{ color: 'white', flexShrink: 0 }} />
                <span style={{ color: 'white', fontSize: '1.05rem' }}>{point}</span>
              </div>
            ))}
          </div>
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            background: 'rgba(255,255,255,0.9)',
            borderRadius: '0.75rem'
          }}>
            <p style={{ color: theme.primary, fontWeight: 'bold', fontSize: '1.1rem' }}>
              Lancez-vous dans l'aventure VAE avec notre accompagnement personnalisé !
            </p>
          </div>
        </div>
      );

    default:
      return null;
  }
};

const VAEPresentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  React.useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => {
          if (prev === slides.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <div style={{ 
      borderRadius: '1rem',
      overflow: 'hidden',
      background: theme.background,
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
    }} data-testid="vae-presentation">
      {/* Slide Content */}
      <div style={{ 
        minHeight: '480px',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <SlideContent slide={slides[currentSlide]} />
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '1rem 2rem',
        borderTop: `1px solid ${theme.accent3}30`,
        background: 'white'
      }}>
        <button 
          onClick={prevSlide}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            border: `1px solid ${theme.accent3}`,
            borderRadius: '0.5rem',
            background: 'white',
            cursor: 'pointer',
            color: theme.text,
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          data-testid="prev-slide-btn"
        >
          <ChevronLeft size={20} />
          <span>Précédent</span>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              border: 'none',
              background: theme.primary,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              transition: 'all 0.2s ease'
            }}
            data-testid="play-pause-btn"
          >
            {isPlaying ? '⏸' : <Play size={18} />}
          </button>
          <span style={{ 
            fontWeight: 'bold',
            color: theme.primary,
            fontSize: '1rem'
          }}>
            {currentSlide + 1} / {slides.length}
          </span>
        </div>

        <button 
          onClick={nextSlide}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            border: 'none',
            borderRadius: '0.5rem',
            background: theme.primary,
            cursor: 'pointer',
            color: 'white',
            fontWeight: '500',
            transition: 'all 0.2s ease'
          }}
          data-testid="next-slide-btn"
        >
          <span>Suivant</span>
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Progress dots */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '0.5rem',
        padding: '1rem',
        background: 'white',
        flexWrap: 'wrap'
      }}>
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              border: 'none',
              background: idx === currentSlide ? theme.primary : theme.accent3,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              opacity: idx === currentSlide ? 1 : 0.5
            }}
            data-testid={`slide-dot-${idx}`}
          />
        ))}
      </div>
    </div>
  );
};

export default VAEPresentation;
